import { isEmpty } from 'lodash-es';
import { paramCleaner } from '@/helpers/formatters';
import { getQuarter } from '@/helpers/date';
import { format } from 'date-fns';
import { postRequest } from './request';

/**
 * Normalizes date range values, falling back to query params or default quarter
 * @param {object} selectedDateRange - Date range object with to/from
 * @param {object} routeQuery - Route query params that may contain to/from
 * @returns {object} Normalized date range with to and from strings
 */
const normalizeDateRange = (selectedDateRange = {}, routeQuery = {}) => {
  const { to: queryTo, from: queryFrom } = routeQuery;
  const { to = queryTo, from = queryFrom } = selectedDateRange;
  const defaultQuarter = getQuarter(1);

  return {
    to: typeof to !== 'undefined' ? to : format(defaultQuarter[1], 'yyyy-MM-dd'),
    from: typeof from !== 'undefined' ? from : format(defaultQuarter[0], 'yyyy-MM-dd'),
  };
};

/**
 * Extracts query parameters, excluding specified keys
 * @param {object} routeQuery - Route query object
 * @param {string[]} excludeKeys - Keys to exclude from param filters
 * @returns {object} Cleaned parameter object
 */
const extractParamFilters = (routeQuery = {}, excludeKeys = []) => {
  const paramFilters = { ...routeQuery };
  excludeKeys.forEach((key) => {
    delete paramFilters[key];
  });
  return paramCleaner(paramFilters);
};

/**
 * Generic filter fetching function
 * @param {object} config - Configuration object
 * @param {string} config.url - API endpoint URL
 * @param {object} config.routeQuery - Route query parameters
 * @param {Array} config.filters - Pre-defined filters array
 * @param {object} config.selectedDateRange - Selected date range (for insights)
 * @param {object} config.owned - Ownership filter
 * @param {string} config.searchTerm - Search term (for library)
 * @param {string} config.mode - Mode parameter (for library)
 * @param {string[]} config.excludeQueryKeys - Keys to exclude from routeQuery
 * @param {number} config.timeout - Request timeout in ms
 * @returns {Promise} API response
 */
const fetchFiltersInternal = async ({
  url,
  routeQuery = {},
  filters = [],
  selectedDateRange = {},
  owned,
  searchTerm,
  mode,
  excludeQueryKeys = [],
  timeout = 60000,
}) => {
  const cleanedParams = extractParamFilters(routeQuery, excludeQueryKeys);

  // Build query string for library endpoints
  const searchParams = new URLSearchParams();

  if (mode) searchParams.set('mode', mode);
  if (typeof routeQuery.sort !== 'undefined') searchParams.append('sort', routeQuery.sort);
  if (typeof routeQuery.page !== 'undefined') searchParams.append('page', routeQuery.page);

  // Build request body
  const body = {
    filters: !isEmpty(filters) ? filters : cleanedParams,
  };

  // Add date range for insights endpoints
  // (always add for insights, conditional for others)
  const isInsightsEndpoint = url.includes('/insights/');
  const hasDateRange = selectedDateRange.to || selectedDateRange.from
    || routeQuery.to || routeQuery.from;
  if (isInsightsEndpoint || hasDateRange) {
    const normalizedDateRange = normalizeDateRange(selectedDateRange, routeQuery);
    body.dateRange = normalizedDateRange;
  }

  // Add ownership and search term for library endpoints
  if (owned !== undefined) body.owned = owned;
  if (searchTerm !== undefined) body.textFilter = searchTerm;

  // Build final URL with query string if needed
  const finalUrl = searchParams.toString() ? `${url}?${searchParams}` : url;

  const response = await postRequest({
    url: finalUrl,
    body,
    timeout,
  });

  return response;
};

export const fetchProcessInsightsFilters = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => fetchFiltersInternal({
  url: '/ae/process-insights/filters',
  routeQuery,
  filters,
  selectedDateRange,
  owned,
  excludeQueryKeys: ['to', 'from', 'owned', 'is_post_revisions_score'],
});

export const fetchSourceInsightsFilters = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => fetchFiltersInternal({
  url: '/ae/source-insights/filters',
  routeQuery,
  filters,
  selectedDateRange,
  owned,
  excludeQueryKeys: ['to', 'from', 'owned'],
});

export const fetchReqLibraryFilters = async ({
  routeQuery = {}, filters = [], owned, searchTerm, mode,
}) => fetchFiltersInternal({
  url: '/requisition/filters',
  routeQuery,
  filters,
  owned,
  searchTerm,
  mode,
  excludeQueryKeys: ['sort', 'page', 'q'],
});

export const fetchJobProfileLibraryFilters = async ({
  routeQuery = {}, filters = [], owned, searchTerm, mode,
}) => fetchFiltersInternal({
  url: '/jobprofile/library/filters',
  routeQuery,
  filters,
  owned,
  searchTerm,
  mode,
  excludeQueryKeys: ['sort', 'page', 'q'],
});

export const fetchEmployeeAcknowledgementsLibraryFilters = async ({
  routeQuery = {}, filters = [], owned, searchTerm, mode,
}) => fetchFiltersInternal({
  url: '/jobprofile/employee-acknowledgement/filters',
  routeQuery,
  filters,
  owned,
  searchTerm,
  mode,
  excludeQueryKeys: ['sort', 'page', 'q'],
});

export const fetchFlsaSurveysLibraryFilters = async ({
  routeQuery = {}, filters = [], owned, searchTerm, mode,
}) => fetchFiltersInternal({
  url: '/jobprofile/flsa-report/filters',
  routeQuery,
  filters,
  owned,
  searchTerm,
  mode,
  excludeQueryKeys: ['sort', 'page', 'q'],
});

export const fetchApprovalsLibraryFilters = async ({
  routeQuery = {}, filters = [], owned, searchTerm, mode,
}) => fetchFiltersInternal({
  url: '/jobprofile/approval-flow/filters',
  routeQuery,
  filters,
  owned,
  searchTerm,
  mode,
  excludeQueryKeys: ['sort', 'page', 'q'],
});
